import React, { useState, useMemo, useCallback } from 'react';
import {
    Box,
    Paper,
    Typography,
    Grid,
    Card,
    CardContent,
    Button,
    Chip,
    TextField,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Tooltip,
    Badge,
    LinearProgress,
    Avatar,
    Stack,
    Divider,
    Drawer,
    Switch,
    FormControlLabel,
    Autocomplete,
    ToggleButton,
    ToggleButtonGroup
} from '@mui/material';
import {
    Inventory as ProductIcon,
    Search as SearchIcon,
    Refresh as RefreshIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as ViewIcon,
    Save as SaveIcon,
    Close as CloseIcon,
    BarChart as StatsIcon,
    Category as CategoryIcon,
    Warning as LowStockIcon,
    LocalOffer as PriceIcon,
    Straighten as DimensionIcon,
    LocalShipping as LogisticsIcon,
    Settings as CustomIcon,
    QrCode as BarcodeIcon,
    History as HistoryIcon,
    AutoGraph as TrendIcon,
    Collections as MediaIcon,
    Label as TagIcon,
    ViewKanban as KanbanIcon,
    TableChart as TableIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
    useProducts,
    useCreateProduct,
    useUpdateProduct,
    useDeleteProduct,
    useProductCategories,
    useProductStats,
    useInventoryStatuses,
    useProductTypes
} from '../../hooks/useProducts';
import SmartDataGrid from '../../components/common/SmartDataGrid';
import KanbanView from '../../components/ui/KanbanView';
import { getCurrencySymbol } from '../../utils/invoice-pdf-general-section-utilities';

const PRODUCT_STATUS_KANBAN_COLUMNS = [
    { id: 'active', label: 'Active', color: '#01B574' },
    { id: 'low_stock', label: 'Low Stock', color: '#FFB547' },
    { id: 'discontinued', label: 'Discontinued', color: '#EE5D50' },
    { id: 'pending', label: 'Pending', color: '#7B61FF' }
];

/**
 * Aurora 2026 Product Cockpit
 * 
 * High-fidelity unified interface for Industrial Product Master Data.
 * Mirrors the Customer Cockpit architecture with SmartDataGrid and Glass Sidebar.
 */
const ProductCockpitPage = () => {
    const { t, i18n } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const formatCurrency = (amount, currency = 'EUR') => {
        const symbol = getCurrencySymbol(currency);
        return `${symbol}${(amount || 0).toLocaleString(i18n?.language || 'en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    // UI State
    const [activeTab, setActiveTab] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [formTab, setFormTab] = useState(0);
    const [viewMode, setViewMode] = useState('table');

    // Data Hooks
    const { data: productsData, isLoading, refetch } = useProducts({
        search: searchTerm,
        category: categoryFilter !== 'all' ? categoryFilter : undefined
    });
    const { data: statsData } = useProductStats();
    const { data: categoriesData } = useProductCategories();
    const { data: statusesData } = useInventoryStatuses();

    const createMutation = useCreateProduct();
    const updateMutation = useUpdateProduct();
    const deleteMutation = useDeleteProduct();

    // Form State (Industrial Fidelity)
    const [formData, setFormData] = useState({
        // Basic Info
        sku: '',
        name: '',
        description: '',
        category: '',
        type: 'physical',
        status: 'active',
        barcode: '',

        // Pricing
        cost_price: 0,
        retail_price: 0,
        wholesale_price: 0,
        msrp: 0,
        currency: 'EUR',
        tax_rate: 20,

        // Marketplace Identifiers
        asin: '',
        upc: '',
        ean: '',
        mpn: '',
        gtin: '',

        // Dimensions & Logistics
        weight: 0,
        weight_unit: 'kg',
        length: 0,
        width: 0,
        height: 0,
        dimension_unit: 'cm',
        volume: 0,

        // Tracking & Storage
        track_expiry: false,
        track_batch: false,
        track_serial: false,
        cold_chain: false,
        storage_type: 'ambient',
        stackable: true,
        reorder_point: 10,
        min_stock: 5,
        max_stock: 100,

        // Custom
        tags: [],
        notes: ''
    });

    const products = useMemo(() => productsData?.items || [], [productsData]);
    const categories = useMemo(() => categoriesData?.categories || [], [categoriesData]);
    const statuses = useMemo(() => statusesData?.statuses || [], [statusesData]);

    // Transform products data for KanbanView
    const kanbanProducts = useMemo(() => {
        return products.map(p => ({
            id: p.id,
            title: p.name || `Product #${p.id}`,
            subtitle: `${p.sku || 'No SKU'} | ${p.category || 'Uncategorized'}`,
            status: p.status || 'active',
            metadata: {
                sku: p.sku,
                category: p.category,
                retail_price: p.retail_price,
                stock_level: p.stock_level,
                currency: p.currency
            }
        }));
    }, [products]);

    const handleRefresh = useCallback(() => refetch(), [refetch]);

    const handleViewModeChange = useCallback((event, newMode) => {
        if (newMode !== null) {
            setViewMode(newMode);
        }
    }, []);

    const handleKanbanStatusChange = useCallback(async (productId, newStatus) => {
        try {
            await updateMutation.mutateAsync({ productId, data: { status: newStatus } });
            enqueueSnackbar(t('product.statusUpdated', 'Product status updated to {{status}}', { status: newStatus }), { variant: 'success' });
            refetch();
        } catch (err) {
            enqueueSnackbar(t('product.statusUpdateError', 'Error updating product status'), { variant: 'error' });
        }
    }, [updateMutation, enqueueSnackbar, t, refetch]);

    const handleOpenDrawer = useCallback((product = null) => {
        if (product) {
            setSelectedProduct(product);
            setFormData({
                sku: product.sku || '',
                name: product.name || '',
                description: product.description || '',
                category: product.category || '',
                type: product.type || 'physical',
                status: product.status || 'active',
                barcode: product.barcode || '',
                cost_price: product.cost_price || 0,
                retail_price: product.retail_price || 0,
                wholesale_price: product.wholesale_price || 0,
                msrp: product.msrp || 0,
                currency: product.currency || 'EUR',
                tax_rate: product.tax_rate || 20,
                asin: product.asin || '',
                upc: product.upc || '',
                ean: product.ean || '',
                mpn: product.mpn || '',
                gtin: product.gtin || '',
                weight: product.weight || 0,
                weight_unit: product.weight_unit || 'kg',
                length: product.length || 0,
                width: product.width || 0,
                height: product.height || 0,
                dimension_unit: product.dimension_unit || 'cm',
                volume: product.volume || 0,
                track_expiry: product.track_expiry || false,
                track_batch: product.track_batch || false,
                track_serial: product.track_serial || false,
                cold_chain: product.cold_chain || false,
                storage_type: product.storage_type || 'ambient',
                stackable: product.stackable !== undefined ? product.stackable : true,
                reorder_point: product.reorder_point || 10,
                min_stock: product.min_stock || 5,
                max_stock: product.max_stock || 100,
                tags: product.tags || [],
                notes: product.notes || ''
            });
        } else {
            setSelectedProduct(null);
            setFormData({
                sku: '', name: '', description: '', category: '', type: 'physical', status: 'active', barcode: '',
                cost_price: 0, retail_price: 0, wholesale_price: 0, msrp: 0, currency: 'EUR', tax_rate: 20,
                asin: '', upc: '', ean: '', mpn: '', gtin: '',
                weight: 0, weight_unit: 'kg', length: 0, width: 0, height: 0, dimension_unit: 'cm', volume: 0,
                track_expiry: false, track_batch: false, track_serial: false, cold_chain: false,
                storage_type: 'ambient', stackable: true, reorder_point: 10, min_stock: 5, max_stock: 100,
                tags: [], notes: ''
            });
        }
        setDrawerOpen(true);
        setFormTab(0);
    }, []);

    const handleCloseDrawer = () => setDrawerOpen(false);

    const handleFormChange = (field) => (event) => {
        const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (selectedProduct) {
                await updateMutation.mutateAsync({ productId: selectedProduct.id, data: formData });
                enqueueSnackbar(t('product.upgradedSuccessfully', 'Product upgraded successfully'), { variant: 'success' });
            } else {
                await createMutation.mutateAsync(formData);
                enqueueSnackbar(t('product.registeredInCatalog', 'Product registered in catalog'), { variant: 'success' });
            }
            handleCloseDrawer();
            refetch();
        } catch (error) {
            enqueueSnackbar(`${t('common.operationFailed', 'Operation Failed')}: ${error.message}`, { variant: 'error' });
        }
    };

    const handleDeleteClick = (product) => {
        setSelectedProduct(product);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = async () => {
        try {
            await deleteMutation.mutateAsync(selectedProduct.id);
            enqueueSnackbar(t('product.removedFromCatalog', 'Product removed from active catalog'), { variant: 'success' });
            setDeleteDialogOpen(false);
            refetch();
        } catch (error) {
            enqueueSnackbar(`${t('common.deletionError', 'Deletion Error')}: ${error.message}`, { variant: 'error' });
        }
    };

    // SmartDataGrid Columns
    const columns = [
        {
            id: 'product',
            label: t('product.identity', 'Product Identity'),
            render: (val, row) => (
                <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar variant="rounded" sx={{ bgcolor: 'secondary.soft', color: 'secondary.main', p: 1 }}>
                        <ProductIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{row.name}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{row.sku}</Typography>
                    </Box>
                </Stack>
            )
        },
        {
            id: 'category',
            label: t('product.technicalCategory', 'Technical Category'),
            render: (val, row) => (
                <Chip label={row.category || '-'} size="small" variant="outlined" icon={<CategoryIcon sx={{ fontSize: '14px !important' }} />} />
            )
        },
        {
            id: 'commercial',
            label: t('product.commercialValue', 'Commercial Value'),
            render: (val, row) => (
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(row.retail_price, row.currency || 'EUR')}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('common.cost', 'Cost')}: {row.cost_price}</Typography>
                </Box>
            )
        },
        {
            id: 'inventory',
            label: t('product.wmsHealth', 'WMS Health'),
            render: (val, row) => (
                <Box sx={{ width: '100px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption">{t('common.stock', 'Stock')}</Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700 }}>{row.stock_level || 0}</Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(((row.stock_level || 0) / (row.max_stock || 100)) * 100, 100)}
                        color={(row.stock_level || 0) <= (row.reorder_point || 0) ? "error" : "success"}
                        sx={{ height: 4, borderRadius: 2 }}
                    />
                </Box>
            )
        },
        {
            id: 'status',
            label: t('product.lifecycle', 'Lifecycle'),
            render: (val, row) => (
                <Chip
                    label={row.status}
                    size="small"
                    color={row.status === 'active' ? 'success' : row.status === 'low_stock' ? 'warning' : 'default'}
                />
            )
        },
        {
            id: 'actions',
            label: t('common.actions', 'Actions'),
            align: 'right',
            render: (val, row) => (
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Tooltip title={t('common.technicalEdit', 'Technical Edit')}><IconButton size="small" color="primary" onClick={() => handleOpenDrawer(row)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title={t('common.archive', 'Archive')}><IconButton size="small" color="error" onClick={() => handleDeleteClick(row)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                </Stack>
            )
        }
    ];

    return (
        <Box sx={{ p: 4, background: 'rgba(244, 247, 254, 0.4)', minHeight: '100vh' }}>
            {/* High-Fidelity Header */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48, boxShadow: '0 4px 12px rgba(67, 24, 255, 0.3)' }}>
                            <ProductIcon fontSize="large" />
                        </Avatar>
                        <Typography variant="h3" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.02em' }}>
                            {t('common.product.cockpitTitle', 'Product Cockpit')}
                        </Typography>
                    </Box>
                    <Typography variant="body1" color="text.secondary" sx={{ ml: 8 }}>
                        {t('common.product.cockpitSubtitle', 'Autonomous Master Data Orchestration for Global Inventory Lifecycle.')}
                    </Typography>
                </Box>
                <Stack direction="row" spacing={2} alignItems="center">
                    <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={handleViewModeChange}
                        size="small"
                    >
                        <ToggleButton value="table" aria-label="Table view">
                            <Tooltip title={t('common.tableView', 'Table View')}>
                                <TableIcon />
                            </Tooltip>
                        </ToggleButton>
                        <ToggleButton value="kanban" aria-label="Kanban view">
                            <Tooltip title={t('common.kanbanView', 'Kanban View')}>
                                <KanbanIcon />
                            </Tooltip>
                        </ToggleButton>
                    </ToggleButtonGroup>
                    <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh}>{t('common.product.telemetryRefresh', 'Telemetry Refresh')}</Button>
                    <Button variant="contained" color="primary" startIcon={<AddIcon />} onClick={() => handleOpenDrawer()} sx={{ boxShadow: '0 4px 14px 0 rgba(0,118,255,0.39)' }}>{t('common.product.catalogOnboarding', 'Catalog Onboarding')}</Button>
                </Stack>
            </Box>

            {/* Industrial Statistics Pulse */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {[
                    { label: t('common.product.totalMasters', 'Total Masters'), value: statsData?.totalProducts || 0, color: '#4318FF', icon: <ProductIcon />, trend: '+2.4%' },
                    { label: t('common.product.activeInCatalog', 'Active in Catalog'), value: statsData?.activeProducts || 0, color: '#01B574', icon: <TrendIcon />, trend: t('common.stable', 'Stable') },
                    { label: t('common.product.criticalStock', 'Critical Stock'), value: statsData?.lowStock || 0, color: '#EE5D50', icon: <LowStockIcon />, trend: '-12%' },
                    { label: t('common.product.globalCategories', 'Global Categories'), value: statsData?.categories || 0, color: '#FFB547', icon: <CategoryIcon />, trend: t('common.product.newCategories', 'New: 2') }
                ].map((stat, i) => (
                    <Grid item xs={12} sm={6} md={3} key={i}>
                        <Card sx={{ borderRadius: '20px', border: 'none', boxShadow: '0px 18px 40px rgba(112, 144, 176, 0.12)' }}>
                            <CardContent sx={{ p: '24px' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box sx={{ width: 56, height: 56, borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(244, 247, 254, 1)', color: stat.color }}>
                                        {stat.icon}
                                    </Box>
                                    <Typography variant="caption" sx={{ color: stat.trend.includes('+') ? 'success.main' : stat.trend.includes('-') ? 'error.main' : 'text.secondary', fontWeight: 700 }}>
                                        {stat.trend}
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.label}</Typography>
                                    <Typography variant="h3" sx={{ fontWeight: 800 }}>{stat.value}</Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Main Operational Grid */}
            <Paper sx={{ p: 0, overflow: 'hidden', borderRadius: '24px', boxShadow: '0px 18px 40px rgba(112, 144, 176, 0.08)', border: '1px solid rgba(233, 239, 250, 0.5)' }}>
                <Box sx={{ p: 3, borderBottom: '1px solid #E9EFFA', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#fff' }}>
                    <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                        <Tab label={t('common.product.allProducts', 'All Products')} sx={{ fontWeight: 700 }} />
                        <Tab label={t('common.product.lowStockAlerts', 'Low Stock Alerts')} sx={{ fontWeight: 700 }} />
                        <Tab label={t('common.product.analyticsView', 'Analytics View')} sx={{ fontWeight: 700 }} />
                    </Tabs>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <FormControl size="small" sx={{ width: 200 }}>
                                                        <InputLabel>{t('common.category', 'Category')}</InputLabel>
                                                        <Select value={categoryFilter} label={t('common.category', 'Category')} onChange={(e) => setCategoryFilter(e.target.value)}>
                                                            <MenuItem value="all">{t('common.allChannels', 'All Channels')}</MenuItem>
                                {categories.map(cat => <MenuItem key={cat.id} value={cat.name}>{cat.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            placeholder={t('common.product.globalSearch', 'Global SKU/Name Search...')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                            sx={{ width: 280 }}
                        />
                    </Stack>
                </Box>
                {isLoading && <LinearProgress color="primary" />}
                {viewMode === 'kanban' ? (
                    <KanbanView
                        columns={PRODUCT_STATUS_KANBAN_COLUMNS}
                        items={kanbanProducts.filter(p =>
                            p.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            p.subtitle?.toLowerCase().includes(searchTerm.toLowerCase())
                        )}
                        onStatusChange={handleKanbanStatusChange}
                        loading={isLoading}
                    />
                ) : (
                    <SmartDataGrid
                        columns={columns}
                        data={products}
                        loading={isLoading}
                        density="comfortable"
                        sx={{ '& .MuiDataGrid-row:hover': { bgcolor: 'rgba(67, 24, 255, 0.02)' } }}
                    />
                )}
            </Paper>

            {/* Industrial Glass Sidebar (Slide-out Drawer) */}
            <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={handleCloseDrawer}
                PaperProps={{
                    sx: {
                        width: { xs: '100%', sm: 700 },
                        background: 'rgba(255, 255, 255, 0.95)',
                        backdropFilter: 'blur(12px)',
                        boxShadow: '-10px 0px 50px rgba(0,0,0,0.1)',
                        borderLeft: '1px solid rgba(255,255,255,0.3)'
                    }
                }}
            >
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ p: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'linear-gradient(90deg, rgba(67, 24, 255, 0.05) 0%, rgba(255,255,255,0) 100%)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'secondary.main', width: 56, height: 56 }}><ProductIcon /></Avatar>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 800 }}>{selectedProduct ? 'Refine Product Master' : 'Create New Master Data'}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <BarcodeIcon fontSize="inherit" /> Industrial Entity Configuration Registry
                                </Typography>
                            </Box>
                        </Box>
                        <IconButton onClick={handleCloseDrawer} sx={{ bgcolor: 'rgba(0,0,0,0.03)' }}><CloseIcon /></IconButton>
                    </Box>

                    <Box sx={{ px: 4, py: 2 }}>
                        <Tabs
                            value={formTab}
                            onChange={(e, v) => setFormTab(v)}
                            variant="scrollable"
                            scrollButtons="auto"
                            sx={{ borderBottom: 1, borderColor: 'divider' }}
                        >
                            <Tab label="Core Identity" icon={<ProductIcon />} iconPosition="start" />
                            <Tab label="Financials" icon={<PriceIcon />} iconPosition="start" />
                            <Tab label="Logistics" icon={<LogisticsIcon />} iconPosition="start" />
                            <Tab label="Technical" icon={<CustomIcon />} iconPosition="start" />
                        </Tabs>
                    </Box>

                    <Box sx={{ p: 4, flex: 1, overflowY: 'auto' }}>
                        <form id="product-form" onSubmit={handleSave}>
                            {formTab === 0 && (
                                <Grid container spacing={3}>
                                    <Grid item xs={12}><TextField fullWidth label="Product Name (Commercial)" required value={formData.name} onChange={handleFormChange('name')} /></Grid>
                                    <Grid item xs={12} sm={6}><TextField fullWidth label="SKU (Stock Keeping Unit)" required value={formData.sku} onChange={handleFormChange('sku')} InputProps={{ sx: { fontFamily: 'monospace' } }} /></Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Category Channel</InputLabel>
                                            <Select value={formData.category} label="Category Channel" onChange={handleFormChange('category')}>
                                                {categories.map(cat => <MenuItem key={cat.id} value={cat.name}>{cat.name}</MenuItem>)}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}><TextField fullWidth label="EAN / Barcode" value={formData.barcode} onChange={handleFormChange('barcode')} /></Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel>Entity Status</InputLabel>
                                            <Select value={formData.status} label="Entity Status" onChange={handleFormChange('status')}>
                                                <MenuItem value="active">Active Monitoring</MenuItem>
                                                <MenuItem value="draft">Catalog Draft</MenuItem>
                                                <MenuItem value="discontinued">End of Life (EOL)</MenuItem>
                                                <MenuItem value="blocked">Safety Block</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}><TextField fullWidth multiline rows={3} label="Technical Specification / Description" value={formData.description} onChange={handleFormChange('description')} /></Grid>
                                </Grid>
                            )}

                            {formTab === 1 && (
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth label="Asset Cost Price" type="number"
                                            value={formData.cost_price} onChange={handleFormChange('cost_price')}
                                            InputProps={{ startAdornment: <InputAdornment position="start">{getCurrencySymbol('EUR')}</InputAdornment> }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth label="Retail Listing Price" type="number"
                                            value={formData.retail_price} onChange={handleFormChange('retail_price')}
                                            InputProps={{ startAdornment: <InputAdornment position="start">{getCurrencySymbol('EUR')}</InputAdornment> }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth label="MSRP (Manufacturer Recommended)" type="number"
                                            value={formData.msrp} onChange={handleFormChange('msrp')}
                                            InputProps={{ startAdornment: <InputAdornment position="start">{getCurrencySymbol('EUR')}</InputAdornment> }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth label="Applied Tax Rate (%)" type="number"
                                            value={formData.tax_rate} onChange={handleFormChange('tax_rate')}
                                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                        />
                                    </Grid>
                                    <Divider sx={{ width: '100%', my: 2 }} />
                                    <Grid item xs={12}><Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.secondary', display: 'flex', alignItems: 'center', gap: 1 }}><TagIcon fontSize="small" /> Marketplace Identification</Typography></Grid>
                                    <Grid item xs={12} sm={6}><TextField fullWidth label="ASIN (Amazon Standard Identification)" value={formData.asin} onChange={handleFormChange('asin')} /></Grid>
                                    <Grid item xs={12} sm={6}><TextField fullWidth label="MPN (Manufacturer Part Number)" value={formData.mpn} onChange={handleFormChange('mpn')} /></Grid>
                                </Grid>
                            )}

                            {formTab === 2 && (
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth label="Gross Weight" type="number"
                                            value={formData.weight} onChange={handleFormChange('weight')}
                                            InputProps={{ endAdornment: <InputAdornment position="end">kg</InputAdornment> }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                            <TextField fullWidth label="L" type="number" value={formData.length} onChange={handleFormChange('length')} />
                                            <TextField fullWidth label="W" type="number" value={formData.width} onChange={handleFormChange('width')} />
                                            <TextField fullWidth label="H" type="number" value={formData.height} onChange={handleFormChange('height')} />
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel control={<Switch checked={formData.track_serial} onChange={handleFormChange('track_serial')} />} label="Serial Number Lifecycle Tracking" />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel control={<Switch checked={formData.track_batch} onChange={handleFormChange('track_batch')} />} label="Batch / Lot Identification" />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel control={<Switch checked={formData.track_expiry} onChange={handleFormChange('track_expiry')} />} label="Expiration Date Monitoring" />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel control={<Switch checked={formData.cold_chain} onChange={handleFormChange('cold_chain')} />} label="Cold Chain Temperature Control" />
                                    </Grid>
                                    <Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
                                    <Grid item xs={12} sm={4}><TextField fullWidth label="Reorder Point" type="number" value={formData.reorder_point} onChange={handleFormChange('reorder_point')} /></Grid>
                                    <Grid item xs={12} sm={4}><TextField fullWidth label="Min Stock" type="number" value={formData.min_stock} onChange={handleFormChange('min_stock')} /></Grid>
                                    <Grid item xs={12} sm={4}><TextField fullWidth label="Max Stock" type="number" value={formData.max_stock} onChange={handleFormChange('max_stock')} /></Grid>
                                </Grid>
                            )}

                            {formTab === 3 && (
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <FormControl fullWidth>
                                            <InputLabel>Storage Infrastructure</InputLabel>
                                            <Select value={formData.storage_type} label="Storage Infrastructure" onChange={handleFormChange('storage_type')}>
                                                <MenuItem value="ambient">Ambient Warehouse</MenuItem>
                                                <MenuItem value="refrigerated">Refrigerated Cluster</MenuItem>
                                                <MenuItem value="hazardous">Hazardous Materials Zone</MenuItem>
                                                <MenuItem value="secure">High-Security Vault</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Autocomplete
                                            multiple
                                            freeSolo
                                            options={[]}
                                            value={formData.tags}
                                            onChange={(e, v) => setFormData(prev => ({ ...prev, tags: v }))}
                                            renderTags={(value, getTagProps) => value.map((option, index) => (<Chip variant="outlined" label={option} {...getTagProps({ index })} />))}
                                            renderInput={(params) => (<TextField {...params} label="Searchable Metadata Tags" placeholder="Add Tag" />)}
                                        />
                                    </Grid>
                                    <Grid item xs={12}><TextField fullWidth multiline rows={4} label="WMS Internal Notes / Handling Instructions" value={formData.notes} onChange={handleFormChange('notes')} /></Grid>
                                </Grid>
                            )}
                        </form>
                    </Box>

                    <Box sx={{ p: 4, borderTop: '1px solid #E9EFFA', bgcolor: '#fff' }}>
                        <Stack direction="row" spacing={2} justifyContent="flex-end">
                            <Button variant="outlined" size="large" onClick={handleCloseDrawer} sx={{ borderRadius: '12px' }}>Cancel</Button>
                            <Button
                                type="submit"
                                form="product-form"
                                variant="contained"
                                color="primary"
                                size="large"
                                startIcon={<SaveIcon />}
                                disabled={createMutation.isPending || updateMutation.isPending}
                                sx={{ borderRadius: '12px', px: 4, bgcolor: 'primary.main', fontWeight: 700 }}
                            >
                                {selectedProduct ? 'Confirm Product Evolution' : 'Initialize Master Data'}
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            </Drawer>

            {/* Archive Confirmation */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} PaperProps={{ sx: { borderRadius: '20px', p: 1 } }}>
                <DialogTitle sx={{ fontWeight: 800 }}>Confirm Lifecycle Termination?</DialogTitle>
                <DialogContent>
                    <Typography color="text.secondary">
                        Are you sure you want to archive "{selectedProduct?.name}"?
                        This operation will suspend catalog visibility and future allocation, but persistent transaction history will be preserved.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} variant="text" color="inherit">Retain Entity</Button>
                    <Button variant="contained" color="error" onClick={confirmDelete} sx={{ borderRadius: '12px', px: 3 }}>Confirm Archive</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default React.memo(ProductCockpitPage);
